import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Clock, MapPin, Loader2, ExternalLink } from 'lucide-react';

// Public, event-branded check-in page — the target of the check-in window QR
// codes for a `conference_checkin` course. No login: the attendee gives a name
// and email, and the server only records the scan. Credit is applied after the
// event by reconciliation (lib/checkin-reconcile.js).
//
// Built for a phone held upright at the venue door. Name and email are kept in
// localStorage so the end-of-day check-out is a single tap.

// Neutral until the event loads, so the page never flashes someone else's brand.
const DEFAULT_BRAND = '#CBD5E1';
const DEFAULT_ACCENT = '#334155';

// Text color for a button on the given background: white whenever it meets
// WCAG AA (4.5:1), since brand buttons read best in white; dark text only for
// backgrounds too light for that.
function inkFor(hex) {
  const n = parseInt(String(hex).slice(1), 16);
  const lin = (c) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return 1.05 / (L + 0.05) >= 4.5 ? '#ffffff' : '#0f172a';
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric',
  });
}

const isCheckout = (label) => /check.?out/i.test(label || '');

function storageKey(slug) { return `event_checkin_${slug}`; }
function loadSaved(slug) {
  try { return JSON.parse(localStorage.getItem(storageKey(slug)) || 'null') || {}; } catch { return {}; }
}
function save(slug, value) {
  try { localStorage.setItem(storageKey(slug), JSON.stringify(value)); } catch { /* private mode */ }
}

export default function EventCheckin() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const code = (params.get('c') || '').trim();

  const [event, setEvent] = useState(null);
  const [win, setWin] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | notfound | error

  const saved = loadSaved(slug);
  const [name, setName] = useState(saved.full_name || '');
  const [email, setEmail] = useState(saved.email || '');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [unregistered, setUnregistered] = useState(false);
  const [done, setDone] = useState(null);
  const emailRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoadState('loading');
    fetch(`/api/events/${encodeURIComponent(slug)}/checkin${code ? `?code=${encodeURIComponent(code)}` : ''}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.status === 404) { setLoadState('notfound'); return; }
        if (!r.ok) throw new Error(String(r.status));
        const d = await r.json();
        setEvent(d.event);
        setWin(d.window);
        setCodeError(d.code_error || null);
        setLoadState('ready');
        if (d.event?.brand_name || d.event?.title) {
          document.title = `Check-in · ${d.event.brand_name || d.event.title}`;
        }
      })
      .catch(() => { if (!cancelled) setLoadState('error'); });
    return () => { cancelled = true; };
  }, [slug, code]);

  const submit = async (confirmUnregistered = false) => {
    setFormError(null);
    if (name.trim().length < 2) { setFormError('Enter your full name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) { setFormError('Enter a valid email address.'); return; }
    setBusy(true);
    try {
      const r = await fetch(`/api/events/${encodeURIComponent(slug)}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code, full_name: name.trim(), email: email.trim(),
          ...(confirmUnregistered ? { confirm_unregistered: true } : {}),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.status === 429) { setFormError('Too many tries from this network. Wait a minute and try again.'); return; }
      if (r.status === 409) { setWin(d.window || win); return; }
      if (!r.ok) { setFormError('Something went wrong. Try again, or see the registration table.'); return; }
      if (d.ok === false && d.registered === false) { setUnregistered(true); return; }
      save(slug, { full_name: name.trim(), email: email.trim() });
      setUnregistered(false);
      setDone(d);
    } catch {
      setFormError('No connection. Check your signal and try again.');
    } finally {
      setBusy(false);
    }
  };

  const brand = event?.brand_color || DEFAULT_BRAND;
  const accent = event?.brand_accent_color || DEFAULT_ACCENT;
  const theme = { '--brand': brand, '--accent': accent, '--accent-ink': inkFor(accent) };

  if (loadState === 'loading') {
    return (
      <Shell theme={theme}>
        <div className="flex-1 flex items-center justify-center text-slate-400">
          <Loader2 className="animate-spin" size={28} />
        </div>
      </Shell>
    );
  }
  if (loadState === 'notfound' || loadState === 'error') {
    return (
      <Shell theme={theme}>
        <Panel>
          <h1 className="text-xl font-bold text-slate-900 mb-2">
            {loadState === 'notfound' ? 'Check-in isn’t available here' : 'We couldn’t load check-in'}
          </h1>
          <p className="text-slate-600">
            {loadState === 'notfound'
              ? 'This link isn’t an active event check-in page. Scan the code on the sign again, or ask at the registration table.'
              : 'Check your connection and reload the page.'}
          </p>
        </Panel>
      </Shell>
    );
  }

  const checkout = isCheckout(win?.label);
  const action = checkout ? 'Check out' : 'Check in';

  return (
    <Shell theme={theme} event={event}>
      {done ? (
        <Panel>
          <div className="flex flex-col items-center text-center py-2">
            <CheckCircle2 size={64} strokeWidth={1.75} style={{ color: 'var(--accent)' }} />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">
              {done.already
                ? `You’re already ${checkout ? 'checked out' : 'checked in'}`
                : `${checkout ? 'Thanks for coming' : 'Welcome'}, ${done.first_name}!`}
            </h1>
            <p className="mt-2 text-slate-600">
              {done.window_label} recorded at <strong>{fmtTime(done.captured_at)}</strong>.
            </p>
            <div className="mt-6 w-full rounded-xl bg-slate-50 border border-slate-200 p-4 text-left text-sm text-slate-700 leading-relaxed">
              {checkout ? (
                <>Your attendance is matched to your registration after the event. Your CE evaluation and
                  certificate will then be ready in your Learnable account.</>
              ) : (
                <>Before you leave, scan the <strong>check-out</strong> code at the end of the day. Both
                  scans are needed for CE credit. This phone will remember your details.</>
              )}
            </div>
          </div>
        </Panel>
      ) : !code || codeError ? (
        <Panel>
          <h1 className="text-xl font-bold text-slate-900 mb-1">Scan the QR code at the venue</h1>
          <p className="text-slate-600">
            {codeError ? 'That link isn’t a current check-in for this event. ' : ''}Use your phone camera to
            scan the <strong>Check in</strong> sign when you arrive, or the <strong>Check out</strong> sign
            before you leave. Can’t scan? See the registration table.
          </p>
        </Panel>
      ) : win.state !== 'open' ? (
        <Panel>
          <div className="flex items-start gap-3">
            <Clock size={28} className="shrink-0 text-slate-400 mt-0.5" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">
                {win.state === 'upcoming' ? `${win.label} isn’t open yet` : `${win.label} has closed`}
              </h1>
              <p className="mt-1 text-slate-600">
                {win.state === 'upcoming'
                  ? <>It opens at <strong>{fmtTime(win.opens_at)}</strong>. Scan the code again then.</>
                  : <>It was open {fmtTime(win.opens_at)}–{fmtTime(win.closes_at)}. If you missed it, see the registration table.</>}
              </p>
            </div>
          </div>
        </Panel>
      ) : (
        <Panel>
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-bold text-slate-900">{win.label}</h1>
            <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
              Open until {fmtTime(win.closes_at)}
            </span>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); submit(false); }} noValidate>
            <label className="block mb-4">
              <span className="block text-sm font-semibold text-slate-700 mb-1.5">Full name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                autoCapitalize="words"
                enterKeyHint="next"
                className={inputCls}
                placeholder="First and last name"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-slate-700 mb-1.5">Email</span>
              <input
                ref={emailRef}
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setUnregistered(false); }}
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                enterKeyHint="go"
                className={inputCls}
                placeholder="The email you registered with"
              />
            </label>

            {formError && <p className="mt-3 text-sm font-medium text-rose-600">{formError}</p>}

            {unregistered ? (
              <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-4">
                <div className="flex gap-2.5">
                  <AlertTriangle size={20} className="shrink-0 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    <p className="font-semibold">We couldn’t find a registration for {email.trim()}.</p>
                    <p className="mt-1">Check the spelling, use the email you registered with, or register below.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setUnregistered(false); emailRef.current?.focus(); emailRef.current?.select(); }}
                  className="mt-4 w-full h-12 rounded-xl bg-white border border-amber-300 font-semibold text-amber-900 active:bg-amber-100"
                >
                  Fix my email
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => submit(true)}
                  className="mt-2 w-full h-12 rounded-xl font-semibold text-amber-900 underline underline-offset-2 disabled:opacity-50"
                >
                  {busy ? 'Saving…' : `${action} anyway`}
                </button>
              </div>
            ) : (
              <PrimaryButton disabled={busy} className="mt-6">
                {busy ? <Loader2 className="animate-spin mx-auto" size={22} /> : action}
              </PrimaryButton>
            )}
          </form>
          <p className="mt-4 text-xs text-slate-500 text-center leading-relaxed">
            No password needed. Your scan is matched to your registration after the event for CE credit.
          </p>
        </Panel>
      )}

      {/* Nothing to register for once the event is over. */}
      {!done && win?.state !== 'closed' && (
        <RegisterPrompt slug={slug} title={event?.title} highlight={unregistered} />
      )}
    </Shell>
  );
}

// Walk-ups who never registered go to the event's course page to register
// and pay, in a new tab so this page is still here to check in afterwards.
function RegisterPrompt({ slug, title, highlight }) {
  return (
    <div
      className={`mt-4 rounded-2xl border p-5 bg-white ${highlight ? 'border-[color:var(--accent)] ring-4 ring-[color:var(--accent)]/15' : 'border-slate-200'}`}
    >
      <h2 className="text-lg font-bold text-slate-900">Haven’t registered?</h2>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">
        Register and pay for {title || 'this event'} on Learnable. Then come back to this page and
        check in with the same email.
      </p>
      <a
        href={`/courses/${encodeURIComponent(slug)}`}
        target="_blank"
        rel="noopener"
        className="mt-4 w-full h-12 rounded-xl border-2 font-semibold inline-flex items-center justify-center gap-2 active:scale-[0.99] transition"
        style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
      >
        Register now <ExternalLink size={16} />
      </a>
    </div>
  );
}

const inputCls =
  'w-full h-14 rounded-xl border border-slate-300 bg-white px-4 text-[17px] text-slate-900 placeholder:text-slate-400 ' +
  'focus:outline-none focus:ring-4 focus:ring-[color:var(--accent)]/20 focus:border-[color:var(--accent)]';

function PrimaryButton({ children, className = '', ...props }) {
  return (
    <button
      type="submit"
      {...props}
      className={`w-full h-14 rounded-xl text-lg font-bold shadow-sm active:scale-[0.99] transition disabled:opacity-50 ${className}`}
      style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
    >
      {children}
    </button>
  );
}

function Panel({ children }) {
  return <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">{children}</div>;
}

function Shell({ theme, event, children }) {
  return (
    <div style={theme} className="min-h-[100dvh] bg-slate-100 flex flex-col">
      <div className="h-1.5" style={{ background: 'var(--brand)' }} />
      <header className="bg-white border-b border-slate-200 px-4 pt-5 pb-4">
        <div className="max-w-md mx-auto">
          {event?.brand_logo_url ? (
            <img src={event.brand_logo_url} alt={event.brand_name || event.title} className="h-20 w-auto max-w-full object-contain" />
          ) : event?.brand_name ? (
            <div className="text-2xl font-bold leading-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: 'var(--accent)' }}>
              {event.brand_name}
            </div>
          ) : null}
          {event && (
            <div className="mt-3">
              <div className="text-[15px] font-bold leading-snug" style={{ color: 'var(--brand)' }}>{event.title}</div>
              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-slate-500">
                {event.live_date_utc && <span>{fmtDate(event.live_date_utc)}</span>}
                {event.venue_name && <span className="inline-flex items-center gap-1"><MapPin size={13} />{event.venue_name}</span>}
              </div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 w-full max-w-md mx-auto px-4 py-5 flex flex-col">{children}</main>
      <footer className="pb-6 pt-2 text-center text-xs text-slate-400">
        Check-in by <span className="font-semibold text-slate-500">Learnable</span>
      </footer>
    </div>
  );
}
